<template>
	<div :class="['step-four', { 'briqpay-overlay': briqpayOverlayActive }]">
		<stepHeader
			:msg="stepHeaderText"
			:currentStep="4"
			:maxStep="4"
			:useSubtitle="!isActive"
			:useBackgroundColor="! isActive || activeStep < 4"
			/>

		<!-- Briqpay Iframe -->
		<div
			v-if="isActive && ! pickupAndPayInStore && paymentType === BRIQPAY && briqpayIframeVisible && pendingAmount"
			ref="checkoutIframe"
		/>

		<!-- Pickup and Pay View -->
		<div
			v-if="activeStep > 3 && isActive && (pickupAndPayInStore || ! pendingAmount)"
			class="checkout-confirmation-wrap"
		>
			<div class="col-xs-12 text-center">
				<div class="checkout-amount">
					{{ pendingAmountFormatted }}
				</div>
				<div v-if="cartSumInsurances > 0" class="sub-title">
					(varav försäkringar {{ cartSumInsurancesFormatted }})
				</div>
				<div class="mb-5">Totalbelopp inkl moms</div>
				<div v-if="orderError" class="alert alert-danger mt-5">
					<span class="glyphicon glyphicon-warning-sign" />
					{{ orderError }}
				</div>

				<!-- Confirm Order Button -->
				<div class="center-block confirm-checkout">
					<text-button
						v-if="! orderIsProcessing"
						class="confirm-order-btn"
						text="Bekräfta beställning"
						size="large"
						width="stretch"
						@click="confirmOrder"
					/>

					<div v-else class="checkout-loading">
						<loading-spinner />
						Ordern behandlas...
					</div>
				</div>

				<div class="pt-5" v-if="! orderIsProcessing">
					Genom att bekräfta beställningen godkänner du Webhallens <a-link class="fake-link" :to="conditionsUrl" target="_blank">köpvillkor</a-link>.<br>
					Du har alltid rätt att utnyttja din ångerrätt. <a-link class="fake-link" :to="regretUrl" target="_blank">Villkor om ångerrätt</a-link>.
				</div>

			</div>
		</div>
	</div>
</template>

<script>
import bus from 'eventbus';
import stepHeader from './step-header';
import localSessionStorage from 'localSessionStorage';
import { isDesktop } from 'mediaQuery';
import { getInsuranceSum, getSelectedServiceObjects } from 'utils/cart';
import { formatPrice } from 'utils';
import { mapState } from 'vuex';
import alert from 'alert';

const BRIQPAY = 1;
const VOUCHER = 3;

export default {
	data() {
		return {
			htmlSnippet: null,
			total: null,
			orderIsProcessing: false,
			orderError: null,
			briqpayOverlayActive: false,
			// 1 = BRIQPAY 3 = VOUCHER
			paymentType: 1,
			BRIQPAY,
			VOUCHER,
			briqpayIframeVisible: true,
		};
	},
	props: {
		activeStep: {
			type: Number,
		},
		isActive: {
			type: Boolean,
			default: false,
		},
		isCorporateCustomer: {
			type: Boolean,
			required: true,
		},
		selectedShippingMethodId: {
			type: Number,
			required:false,
			default: 0,
		},
		selectedShippingOption: {
			type: Object,
			required:false,
			default: () => {},
		},
		isStoreReservation: false,
		selectedStoreId: {
			type: Number,
			required:false,
			default: 0,
		},
		pickupAndPayInStore: {
			type: Boolean,
			default: false,
		},
		customerInfoData: {
			type: Object,
			default: () => {},
			required: true,
		},
		vouchers: {
			type: Array,
		},
		paymentLink: {
			type: String,
			default: null,
		},
		cart: {
			type: Object,
			required: true,
		},
		pendingAmount: {
			type: Number,
			required: true,
		},
	},
	components: {
		stepHeader,
	},
	mounted () {
		bus.$on('total-amount', this.setTotal);
		window.addEventListener('beforeunload', this.beforeUnloadHandler);

		let checkoutData = localSessionStorage.get('checkoutData');
		if (checkoutData) {
			checkoutData = JSON.parse(checkoutData);
			if (checkoutData?.returningFromBriqpay) {
				setTimeout(() => {
					localSessionStorage.remove('checkoutData');
					const newData = {
						returningFromBriqpay: false
					};

					localSessionStorage.set('checkoutData', JSON.stringify(newData));
				}, 5000);
			}
		}

		this.processOrderEvent = (event) => {
			if (event.data.messageType === 'make_decision') {
				this.processOrder();
			}
		};

		window.addEventListener('message', this.processOrderEvent);
	},
	beforeDestroy () {
		this.beforeUnloadHandler();
		window.removeEventListener('message', this.processOrderEvent);
		window.removeEventListener('beforeunload', this.beforeUnloadHandler);
	},
	methods: {
		beforeUnloadHandler () {
			if (! this.orderIsProcessing) {
				localSessionStorage.remove('checkoutData');
				const newData = {
					returningFromBriqpay: false
				};

				localSessionStorage.set('checkoutData', JSON.stringify(newData));
				window.removeEventListener('beforeunload', this.beforeUnloadHandler);
			}
		},
		setTotal (amount) {
			this.total = amount;
		},
		loadPayments () {
			if (! this.pickupAndPayInStore) {
				waja.post('checkout/briqpay')
					.data(this.orderData)
					.on('success', res => {
						this.htmlSnippet = res.data.htmlSnippet;
						this.embedSdkIframe();
					}).go();
			}
		},
		async reloadPayments () {
			const order = {
				shippingMethod: this.selectedShippingMethodId,
				giftCards: this.vouchers?.filter(v => v.type === 'giftCard').map(v => v.code),
				discountCodes: this.vouchers?.filter(v => v.type === 'discountCode').map(v => v.code),
				paymentLink: this.paymentLink || undefined,
			};

			if (window._briqpay?.v3?.suspend) {
				console.log('Suspending Briqpay...');
				window._briqpay.v3.suspend();
			} else {
				console.warn('Briqpay suspend not available');
			}

			try {
				await new Promise((resolve, reject) => {
				waja.post('checkout/briqpay/update')
					.data(order)
					.on('success', res => {
						resolve();
					})
					.on('error', reject)
					.go();
				});

				setTimeout(() => {
					if (window._briqpay?.v3?.resume) {
						console.log('Resuming Briqpay...');
						window._briqpay.v3.resume();
					} else {
						console.warn('Briqpay resume not available');
					}
				}, 100);

			} catch (err) {
				console.error('Failed to update session:', err);
				if (window._briqpay?.v3?.resume) {
					window._briqpay.v3.resume();
				}
			}
		},
		embedSdkIframe () {
			const container = this.$refs.checkoutIframe;
			container.innerHTML = this.htmlSnippet;
			const scriptTags = container.getElementsByTagName('script');

			for (let i = 0; i < scriptTags.length; i++) {
				const oldScript = scriptTags[i];
				const newScript = document.createElement('script');
				newScript.type = 'text/javascript';
				newScript.text = oldScript.text;
				oldScript.parentNode.replaceChild(newScript, oldScript);
			}

			if (typeof _briqpay !== 'undefined' && _briqpay.subscribe) {
				_briqpay.subscribe('paymentProcessStarted', () => {
					if (container) {
						console.log('PaymentProcessStarted');
						container.style.position = "relative";
						container.style.zIndex = "9999";
						this.briqpayOverlayActive = true;
						this.$emit('showBriqpayOverlay');
					}
				});
				_briqpay.subscribe('paymentProcessCancelled', () => {
					if (container) {
						console.log('paymentProcessCancelled');
						container.style.position = "";
						container.style.zIndex = "";
						this.briqpayOverlayActive = false;
						this.$emit('hideBriqpayOverlay');
					}
				});
			}
		},
		processOrder () {
			this.orderIsProcessing = true;
			const checkoutData = {
				returningFromBriqpay: true,
				activeStep: 4,
				isCorporateCustomer: this.isCorporateCustomer,
				isStoreReservation: this.isStoreReservation,
				selectedShippingMethodId: this.selectedShippingMethodId,
				selectedShippingOption: this.selectedShippingOption,
				selectedStoreId: this.selectedStoreId,
				cutomerInfoData: this.customerInfoData,
			};

			const discountCodes = [];
			const giftCards = [];
			for (const voucher of this.vouchers) {
				if (voucher.type === 'giftCard') {
					giftCards.push(voucher.code);
				} else if (voucher.type === 'storeCredit') {
					useStoreCredit = true;
				} else if (voucher.type === 'discountCode') {
					discountCodes.push(voucher.code);
				}
			}

			localSessionStorage.set('checkoutData', JSON.stringify(checkoutData));

			new Promise((resolve) => {
				waja.post('checkout/briqpay/process')
					.data({
						touchpoint: isDesktop() ? 'DESKTOP' : 'MOBILE',
						shippingSiteId: localSessionStorage.get('checkout:shippingSiteId'),
						paymentLink: this.paymentLink || undefined,
						discountCodes: discountCodes || undefined,
						giftCards: giftCards || undefined,
					})
					.on('success', res => {
						window._briqpay.v3.resumeDecision();
						resolve();
					})
					.on('error', err => {
						this.orderIsProcessing = false;
						window._briqpay.v3.resumeDecision();
						resolve();
					}).go();
			});
		},
		confirmOrder () {
			const giftCards = [];
			let useStoreCredit = false;
			const discountCodes = [];
			for (const voucher of this.vouchers) {
				if (voucher.type === 'giftCard') {
					giftCards.push(voucher.code);
				} else if (voucher.type === 'storeCredit') {
					useStoreCredit = true;
				} else if (voucher.type === 'discountCode') {
					discountCodes.push(voucher.code);
				}
			}

			// Extra fraktalternativ, ex postombud eller val av leveransdag
			let shippingOptionCode = null;
			let shippingOption = null;
			if (this.selectedShippingOption) {
				shippingOption = {
					value: this.selectedShippingOption.value,
					type: this.selectedShippingOption.type,
				};
				// These are not needed in the shippingOptionCode
				delete this.selectedShippingOption.value
				delete this.selectedShippingOption.type

				shippingOptionCode = JSON.stringify(this.selectedShippingOption);
			}
			const phone = this.pickupAndPayInStore ? this.customerInfoData.phone : this.customerInfoData.mobile;

			const order = {
				currency: this.cart.currency,
				customerInfo: {
					...this.customerInfoData,
					phone,
				},
				paymentMethod: ! this.pendingAmount ? 24 : 2,
				isCorporateCustomer: this.isCorporateCustomer,
				shippingMethod: this.isStoreReservation ? 4 : this.selectedShippingMethodId === 100 ? 1 : this.selectedShippingMethodId,
				shippingServiceCode: localSessionStorage.get('checkout:selectedShippingServiceCode'),
				shippingOptionCode,
				shippingOption,
				shippingSiteId: localSessionStorage.get('checkout:shippingSiteId'),
				rows: this.cart.rows.map(row => {
					const orderRow = {
						productId: row.product.id,
						unitPrice: row.unitPrice,
						quantity: row.quantity,
						pass: row.pass,
						extraData: row.extraData,
						isSentFromStore: row.product.stock.isSentFromStore
					};

					// Attach any services (insurances/warranties) selected via row.extraData.services ({id: true}).
					const selectedServices = getSelectedServiceObjects(row);
					if (selectedServices.length) {
						orderRow.services = selectedServices.map(service => ({
							productId: service.id,
							unitPrice: service.price,
						}));
					}

					return orderRow;
				}),
				perk: this.selectedPerk ? this.selectedPerk.id : null,
				giftCards,
				useStoreCredit,
				discountCodes: discountCodes,
				partialDelivery: this.partialDelivery,
			};

			if (this.paymentLink) {
				order.paymentLink = this.paymentLink;
			} else {
				order.cartId = this.cart.id;
			}

			if (this.isStoreReservation) {
				order.storeId = this.selectedStoreId;
			}

			this.orderIsProcessing = true;

			waja.post('order')
				.data({
					order,
					touchpoint: isDesktop() ? 'DESKTOP' : 'MOBILE',
				})
				.on('success', res => {
					let url;

					url = '/checkout-complete/' + res.data.orderId;
					if (res.data.secret) {
						url += '?secret=' + res.data.secret;
					}

					this.orderIsProcessing = false;
					this.beforeUnloadHandler();

					this.$router.push(url, () => {
						this.$store.dispatch('cart/wasDestroyed');
					});
				})
				.on('error', res => {
					if (res.status === 400) {
						this.orderIsProcessing = false;
						res.json().then(json => {
							// @TODO vi kanske ska ha en map med olika nycklar och fraser för dem ist för fula ifar
							if (json.errors && (json.errors['order.customerInfo.street_address'] || json.errors['order.customerInfo.city'])) {
								alert('Adressen du matat in är ogiltig! Vänligen korrigera felet och försök igen.');
							} else if (json.errors && json.errors['order.customerInfo.zip_code']) {
								alert('Postnumret du matat in är ogiltigt! Vänligen korrigera felet och försök igen.');
							} else if (json.errors && json.errors['order.customerInfo.email']) {
								alert('E-postadressen du matat in är ogiltig! Vänligen korrigera felet och försök igen.');
							} else if (json.errors && json.errors['order.customerInfo.phone']) {
								const phoneType = this.pickupAndPayInStore ? 'Telefonnumret' : 'Mobilnumret';
								alert(phoneType + ' du matat in är ogiltigt! Vänligen korrigera felet och försök igen.');
							} else if (json.errors && (json.errors['order.customerInfo.first_name'] || json.errors['order.customerInfo.last_name'])) {
								alert('Du har inte matat in namn och/eller efternamn! Vänligen korrigera felet och försök igen.');
							} else {
								alert('Du har matat in ogiltiga uppgifter!');
							}
						})
					} else if (res.status === 403) {
						this.orderIsProcessing = false;
						res.json().then(json => {
							this.orderError = json.errorMessage;
							// if (json.noRetry) { // ingen mening med att försöka igen med samma betalsätt
							// 		this.selectedPaymentMethod = null;
							// }
						});
					} else {
						this.orderIsProcessing = false;
						alert('Något gick fel. Vänligen kontrollera att alla fält är korrekt ifyllda och försök igen.');
					}
				}).go();
		},
	},
	computed: {
		orderData () {
			const giftCards = this.vouchers?.filter(v => v.type === 'giftCard').map(v => v.code) || [];
			const discountCodes = this.vouchers?.filter(v => v.type === 'discountCode').map(v => v.code) || [];

			let shippingOptionCode = undefined;
			if (this.selectedShippingOption) {
				shippingOptionCode = {
					value: this.selectedShippingOption.value,
					type: this.selectedShippingOption.type
				};
			}


			return {
				customerType: this.isCorporateCustomer ? 'business' : 'consumer',
				shippingMethod: this.selectedShippingMethodId,
				customerInfo: this.briqPayCustomerInfo,
				giftCards,
				discountCodes,
				storeId: this.isStoreReservation ? this.selectedStoreId : undefined,
				paymentLink: this.paymentLink || undefined,
				shippingOptionCode,
			};
   		},
		stepHeaderText () {
			return this.pickupAndPayInStore ? 'Bekräfta Beställning' : 'Välj Betalsätt';
		},
		pendingAmountFormatted () {
			return formatPrice(this.pendingAmount, 'SEK');
		},
		cartSumInsurancesFormatted () {
			return formatPrice(this.cartSumInsurances, this.cart.currency);
		},
		cartSumInsurances () {
			return getInsuranceSum(this.cart);
		},
		briqPayCustomerInfo () {
			const phone = this.pickupAndPayInStore ? this.customerInfoData.phone : this.customerInfoData.mobile;
			let customerData = {
				...(! this.isCorporateCustomer && {
					consumer: {
						name: this.customerInfoData.first_name + ' ' + this.customerInfoData.last_name,
					},
				}),
				...(this.isCorporateCustomer && {
					company: {
						// cin: this.customerInfoData.corporate_number,
						// name: this.customerInfoData.corporate_name,
						...(! this.isStoreReservation && {
							deliveryAttention: this.customerInfoData.delivery_attention,
						}),
					},
				}),
				billing: {
					...(this.isCorporateCustomer && {
						companyName: this.customerInfoData.corporate_name,
						cin: this.customerInfoData.corporate_number,
					}),
					...(! this.isCorporateCustomer && {
						firstName: this.customerInfoData.first_name,
						lastName: this.customerInfoData.last_name,
					}),
					streetAddress: this.customerInfoData.street_address,
					zip: this.customerInfoData.zip_code,
					city: this.customerInfoData.city,
					phoneNumber: phone,
					email: this.customerInfoData.email,
				},
				...(this.isCorporateCustomer
					&& ((this.customerInfoData.delivery_email.length && this.customerInfoData.email !== this.customerInfoData.delivery_email)
					|| (this.customerInfoData.delivery_phone.length && this.customerInfoData.phone !== this.customerInfoData.delivery_phone)
					|| (this.customerInfoData.delivery_zip_code.length && this.customerInfoData.zip_code !== this.customerInfoData.delivery_zip_code)
					|| (this.customerInfoData.delivery_street_address.length && this.customerInfoData.street_address !== this.customerInfoData.delivery_street_address)) && {
					shipping: {
						companyName: this.customerInfoData.corporate_name,
						cin: this.customerInfoData.corporate_number,
						streetAddress: this.customerInfoData.delivery_street_address,
						zip: this.customerInfoData.delivery_zip_code,
						city: this.customerInfoData.delivery_city,
						phoneNumber: this.customerInfoData.delivery_phone,
						email: this.customerInfoData.delivery_email,
					},
				}),
			};

			return customerData;
		},
		conditionsUrl () {
			return this.$router.route({
				name: 'info',
				params: {
					id: 8,
					name: 'Köpvillkor',
				},
			});
		},
		regretUrl () {
			return this.$router.route({
				name: 'info',
				params: {
					id: 15,
					name: 'Ångerrät och öppet köp',
				},
			});
		},
		...mapState({
			user: state => state.user
		}),

	},
	watch: {
		activeStep (to) {
			if (to === 4 && ! this.pickupAndPayInStore && this.paymentType === BRIQPAY) {
				this.loadPayments();
			}
		},
		total (to, frm) {
			if (frm == null || to == null || to === frm) {
        		return;
    		}

			if (this.activeStep === 4 && this.isActive && !this.pickupAndPayInStore) {
				console.log('[Briqpay] reloadPayments triggered', {
					to,
					frm,
					activeStep: this.activeStep,
					isActive: this.isActive,
					pickupAndPayInStore: this.pickupAndPayInStore,
				});
				this.reloadPayments();
			}
		},
		isCorporateCustomer (to) {
			if (! to) {
				this.paymentType = BRIQPAY;
			}
		},
		pendingAmount (to) {
			if (! to) {
				this.paymentType = VOUCHER;
			} else if (to && this.paymentType === VOUCHER){
				this.paymentType = BRIQPAY;
				if (this.isCorporateCustomer) {
					bus.$emit('stepUpdated', 3, null);
				} else {
					let emptyField = false;
					for (const group of Object.keys(this.briqPayCustomerInfo)) {
						for (const field of Object.keys(this.briqPayCustomerInfo[group])) {
							if (! this.briqPayCustomerInfo[group][field].length) {
								emptyField = true;
								break;
							}
						}
					}

					if (emptyField) {
						bus.$emit('stepUpdated', 3, null);
					}
				}
			}
		},
		boughtInsurance (to) {
			/**
			 * If any required insurance info is missing, send
			 * customer back to step 3
			 */
			if (to && this.isActive
				&& (! this.customerInfoData.hasOwnProperty('first_name')
				|| ! this.customerInfoData.hasOwnProperty('last_name')
				|| ! this.customerInfoData.hasOwnProperty('street_address')
				|| ! this.customerInfoData.hasOwnProperty('zip_code'))) {
				bus.$emit('stepUpdated', 3, null);
			}
		},
		paymentType (to, from) {
			if (to === VOUCHER) {
				this.briqpayIframeVisible = false;

				if (window._briqpay?.v3?.suspend) {
					window._briqpay.v3.suspend();
				}
				const container = this.$refs.checkoutIframe;
				if (container) container.innerHTML = '';
			}

			if (to === BRIQPAY) {
				// Force re-render and reload
				this.briqpayIframeVisible = false;
				this.$nextTick(() => {
					this.briqpayIframeVisible = true;
					this.$nextTick(() => {
						this.loadPayments();
					});
				});
			}
		},
		briqpayOverlayActive (to) {
			this.orderIsProcessing = to;
			if (! to) {
				localSessionStorage.remove('checkoutData');
				const newData = {
					returningFromBriqpay: false
				};

				localSessionStorage.set('checkoutData', JSON.stringify(newData));
			}
		},
	},
};
</script>
